import { SCHEDULING_LIMITS, schedulingAvailabilitySnapshotSchema, schedulingAvailabilityWindowSchema, schedulingIntervalSchema, schedulingPublishInputSchema, } from "./scheduling.js";
const MINUTE = 60_000;
const read = ({ start, end }) => ({ start: Date.parse(start), end: Date.parse(end) });
const write = ({ start, end }) => ({
    start: new Date(start).toISOString(), end: new Date(end).toISOString(),
});
function* alignedStarts(first, last, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-GB", { timeZone, minute: "2-digit", second: "2-digit" });
    for (let start = Math.ceil(first / MINUTE) * MINUTE; start <= last; start += MINUTE) {
        const parts = formatter.formatToParts(start);
        const minute = Number(parts.find(({ type }) => type === "minute")?.value);
        const second = Number(parts.find(({ type }) => type === "second")?.value);
        if (minute % SCHEDULING_LIMITS.startStepMinutes === 0 && second === 0)
            yield start;
    }
}
export class SchedulingAvailabilityUnknown extends Error {
    constructor() { super("The selected calendars could not all be checked. Try again."); }
}
/** Half-open intervals; adjacent and overlapping windows form one available window. */
export function mergeSchedulingIntervals(values) {
    const sorted = values.map(({ start, end }) => read(schedulingIntervalSchema.parse({ start, end })))
        .sort((a, b) => a.start - b.start);
    const merged = [];
    for (const value of sorted) {
        const previous = merged.at(-1);
        if (previous && value.start <= previous.end)
            previous.end = Math.max(previous.end, value.end);
        else
            merged.push({ ...value });
    }
    return merged.map(write);
}
function verifiedBusy(input) {
    const { proposal, window, now } = input;
    const snapshot = schedulingAvailabilitySnapshotSchema.parse(input.snapshot);
    const coverage = read(snapshot.window);
    const observed = Date.parse(snapshot.observedAt);
    const ids = snapshot.calendars.map(({ calendarId }) => calendarId);
    if (coverage.start > window.start || coverage.end < window.end || observed > now
        || now - observed > SCHEDULING_LIMITS.availabilityMaxAgeMs || new Set(ids).size !== ids.length
        || proposal.conflictCalendarIds.some((id) => !ids.includes(id)))
        throw new SchedulingAvailabilityUnknown();
    const busy = [];
    for (const id of proposal.conflictCalendarIds) {
        const calendar = snapshot.calendars.find(({ calendarId }) => calendarId === id);
        if (!calendar || calendar.status !== "complete")
            throw new SchedulingAvailabilityUnknown();
        busy.push(...calendar.busy);
    }
    return mergeSchedulingIntervals(busy).map(read);
}
/**
 * Server-owned slot policy: host-wall-clock quarter hours, elapsed meeting duration.
 * Walking instants (not local date strings) retains both fall-back occurrences and
 * skips nonexistent spring-forward times. A guest timezone only formats these slots.
 */
export function schedulingSlots(input) {
    const proposal = schedulingPublishInputSchema.parse(input.proposal);
    const window = read(schedulingAvailabilityWindowSchema.parse(input.window));
    if (!Number.isSafeInteger(input.now))
        throw new Error("Expected the server clock in milliseconds");
    const busy = verifiedBusy({ ...input, proposal, window });
    if (input.now >= Date.parse(proposal.expiresAt))
        return [];
    const duration = proposal.durationMinutes * MINUTE;
    const earliest = input.now + proposal.minimumNoticeMinutes * MINUTE;
    const slots = [];
    for (const range of mergeSchedulingIntervals(proposal.ranges).map(read)) {
        const first = Math.ceil(Math.max(range.start, window.start, earliest) / MINUTE) * MINUTE;
        const last = Math.min(range.end - duration, window.end - 1);
        for (const start of alignedStarts(first, last, proposal.timeZone)) {
            const end = start + duration;
            // Coverage must include the entire meeting, including one crossing the display window.
            if (end > Date.parse(input.snapshot.window.end))
                continue;
            if (!busy.some((taken) => start < taken.end && end > taken.start))
                slots.push(write({ start, end }));
        }
    }
    return slots;
}
/** Publication is time-sensitive; schema parsing alone must never publish stale ranges. */
export function assertSchedulingPublishable(proposal, now) {
    const parsed = schedulingPublishInputSchema.parse(proposal);
    if (!Number.isSafeInteger(now))
        throw new Error("Expected the server clock in milliseconds");
    if (Date.parse(parsed.expiresAt) <= now)
        throw new Error("The proposal has expired");
    if (parsed.ranges.some(({ end }) => Date.parse(end) > now + SCHEDULING_LIMITS.horizonDays * 86_400_000)) {
        throw new Error("Availability must be within the next 90 days");
    }
    const earliest = now + parsed.minimumNoticeMinutes * MINUTE;
    if (!mergeSchedulingIntervals(parsed.ranges).some(({ start, end }) => !alignedStarts(Math.max(Date.parse(start), earliest), Date.parse(end) - parsed.durationMinutes * MINUTE, parsed.timeZone).next().done)) {
        throw new Error("No upcoming range can fit this meeting");
    }
}
